import type { Account, Consent, LoginRequest, LoginResponse, PaymentHistory, SignupRequest, UpdateFcmTokenResponse } from '@/types/account.types'
import type { ApiCursorBody, ApiResponse } from '@/types/api-response.types'
import type { UploadFileResponse } from '@/types/upload-file.types'
import api from '../axios'

export const accountService = {
  async updateConsent(consent: Consent): Promise<Consent> {
    const response: ApiResponse<Consent> = await api.put('/v1/account/consent', {
      subscribedAds: consent.subscribedAds,
    })
    return response.body
  },

  async withdrawal(): Promise<void> {
    const response: ApiResponse<void> = await api.post('/v1/account/withdrawal')
    return response.body
  },

  async uploadProfileImage(image: File): Promise<UploadFileResponse> {
    const response: ApiResponse<UploadFileResponse> = await api.post('/v1/account/upload/profile', {
      file: image,
    }, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.body
  },

  async updateProfileImage(profileIdx: number | null): Promise<void> {
    const response: ApiResponse<void> = await api.put('/v1/account/profile', {
      profileIdx: profileIdx,
    })
    return response.body
  },

  async signup(signupRequest: SignupRequest): Promise<LoginResponse> {
    const response: ApiResponse<LoginResponse> = await api.post('/v1/account/signup', {
      socialKey: signupRequest.socialKey,
      subSocialKey: signupRequest.subSocialKey,
      socialType: signupRequest.socialType,
      subscribedAds: signupRequest.subscribedAds,
    })
    return response.body
  },

  async logout(refreshToken: string): Promise<void> {
    const response: ApiResponse<void> = await api.post('/v1/account/logout', {
      refreshToken: refreshToken,
    })
    return response.body
  },

  async login(loginRequest: LoginRequest): Promise<LoginResponse> {
    const response: ApiResponse<LoginResponse> = await api.post('/v1/account/login', {
      socialKey: loginRequest.socialKey,
      subSocialKey: loginRequest.subSocialKey,
      socialType: loginRequest.socialType,
    })
    return response.body
  },

  async selfAuthentication(receiptId: string): Promise<void> {
    const response: ApiResponse<void> = await api.post(`/v1/account/auth/${receiptId}`)
    return response.body
  },

  async getAccessToken(refreshToken: string): Promise<LoginResponse> {
    const response: ApiResponse<LoginResponse> = await api.post('/v1/account/access-token', {
      refreshToken: refreshToken,
    })
    return response.body
  },

  async patchFcmToken(fcmToken: string): Promise<UpdateFcmTokenResponse> {
    const response: ApiResponse<UpdateFcmTokenResponse> = await api.patch('/v1/account/fcm-token', {
      fcmToken: fcmToken,
    })
    return response.body
  },

  async getPaymentHistory(cursor: number | null, size: number, ascending: boolean): Promise<ApiCursorBody<PaymentHistory>> {
    const response: ApiResponse<ApiCursorBody<PaymentHistory>> = await api.get('/v1/account/payment-history', {
      params: {
        cursor,
        size,
        ascending,
      },
    })
    return response.body
  },

  async getMyAccount(): Promise<Account> {
    const response: ApiResponse<Account> = await api.get('/v1/account/my')
    return response.body
  },
}

